/**
 * CASE.SHE — бічний кошик: швидке додавання з блоку «Доповни свій образ».
 *
 * Розмітку рендерить `snippets/case-she-cart-recommendation.liquid` усередині
 * `.cart-drawer__inner`, тож після кожної зміни кошика Horizon перемальовує її
 * сам (Section Rendering API + morph). Тут немає власного рендеру і власної
 * резолюції варіантів: ID варіанта береться з `<select>` або прихованого поля
 * картки в момент натискання.
 *
 * Варіант передзамовлення йде в кошик з тими самими властивостями рядка, що й
 * з форми товару (`Статус`, `Строк очікування`): картка позначає такий варіант
 * атрибутом `data-cs-preorder` і тримає властивості в `<template>`
 * (`snippets/case-she-cart-recommendation.liquid`). Обидва читаються в момент
 * натискання, як і ID варіанта, тож зміна вибору в `<select>` не лишає
 * застарілих властивостей. Звичайний варіант іде без `properties`.
 *
 * Потік додавання повторює штатний `product-form.js`: `/cart/add.js` з
 * параметром `sections`, потім `/cart.js` і стандартна `CartLinesUpdateEvent`.
 * На цю подію вже підписані `cart-items-component` (морф drawer),
 * `cart-icon` (лічильник) і `header-actions` (live-region з кількістю), тому
 * нічого з цього тут не дублюється. Подія надсилається лише після успішного
 * додавання: помилка не змінює кошик, і тоді drawer не перемальовується, а
 * повідомлення лишається в картці.
 *
 * Морф відновлює серверну розмітку карток, тож стан, який має пережити
 * оновлення drawer (обраний варіант, запит у дорозі), живе тут, у модулі, і
 * повертається в картки після кожного оновлення.
 */
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';
import { cartErrorMessage, logCartError, shopifyCartErrorText } from '@theme/case-she-cart-errors';

const SOURCE = 'case-she-cart-recommendations';
const SECTION_ID = 'cart-drawer-section';

/** Обраний покупцем варіант за ID товару. @type {Map<string, string>} */
const selections = new Map();

/** Товари, для яких запит на додавання ще в дорозі. @type {Set<string>} */
const inFlight = new Set();

/** Дочекатися, поки штатні обробники події встигнуть перемалювати drawer. */
function afterRender() {
  return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve(undefined))));
}

/** @param {string} productId */
function cardOf(productId) {
  const card = document.getElementById(`cs-cart-rec-${productId}`);
  return card instanceof HTMLElement ? card : null;
}

/**
 * @param {Element} card
 * @returns {HTMLSelectElement | null}
 */
function selectOf(card) {
  const select = card.querySelector('[data-cs-rec-select]');
  return select instanceof HTMLSelectElement ? select : null;
}

/**
 * ID варіанта, який піде в кошик, або порожній рядок, якщо варіант ще не обрано.
 * @param {HTMLElement} card
 * @returns {string}
 */
function selectedVariantId(card) {
  const select = selectOf(card);
  if (select) return select.value;

  const input = card.querySelector('[data-cs-rec-variant]');
  return input instanceof HTMLInputElement ? input.value : '';
}

/**
 * Властивості рядка кошика для обраного варіанта: для передзамовлення — поля
 * з `<template>` картки, інакше `null` (запит іде без `properties`).
 * @param {HTMLElement} card
 * @returns {Record<string, string> | null}
 */
function selectedLineItemProperties(card) {
  const select = selectOf(card);
  const source = select ? select.selectedOptions[0] : card.querySelector('[data-cs-rec-variant]');
  if (!source?.hasAttribute('data-cs-preorder')) return null;

  const template = card.querySelector('template[data-cs-rec-preorder-properties]');
  if (!(template instanceof HTMLTemplateElement)) return null;

  /** @type {Record<string, string>} */
  const properties = {};
  for (const input of template.content.querySelectorAll('input[name^="properties["]')) {
    if (!(input instanceof HTMLInputElement)) continue;
    const name = input.name.slice('properties['.length, -1);
    if (name) properties[name] = input.value;
  }
  return Object.keys(properties).length > 0 ? properties : null;
}

/**
 * Ціна обраного варіанта. До вибору лишається серверна «від …».
 * @param {HTMLElement} card
 */
function renderPrice(card) {
  const option = selectOf(card)?.selectedOptions[0];
  if (!option || !option.value) return;

  const current = card.querySelector('[data-cs-rec-price-current]');
  const compare = card.querySelector('[data-cs-rec-price-compare]');
  if (current) current.textContent = option.dataset.price ?? '';
  if (compare instanceof HTMLElement) {
    const comparePrice = option.dataset.comparePrice ?? '';
    compare.textContent = comparePrice;
    compare.hidden = comparePrice === '';
  }
}

/**
 * @param {HTMLElement} card
 * @param {string} message
 */
function showError(card, message) {
  const error = card.querySelector('[data-cs-rec-error]');
  if (!(error instanceof HTMLElement) || !message) return;
  error.textContent = message;
  error.hidden = false;
}

/** @param {HTMLElement} card */
function hideError(card) {
  const error = card.querySelector('[data-cs-rec-error]');
  if (error instanceof HTMLElement) {
    error.hidden = true;
    error.textContent = '';
  }
  selectOf(card)?.removeAttribute('aria-invalid');
}

/**
 * Позначити кнопку картки зайнятою або вільною. Ідемпотентно: викликається і
 * після морфу, який скидає атрибути до серверних.
 * @param {HTMLElement} card
 * @param {boolean} busy
 */
function setBusy(card, busy) {
  const button = card.querySelector('[data-cs-rec-add]');
  if (!(button instanceof HTMLButtonElement)) return;
  const label = button.querySelector('[data-cs-rec-add-text]');

  if (busy) {
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');
    if (label instanceof HTMLElement && card.dataset.addingLabel && label.dataset.idleText === undefined) {
      label.dataset.idleText = label.textContent ?? '';
      label.textContent = card.dataset.addingLabel;
    }
    return;
  }

  button.removeAttribute('aria-busy');
  button.removeAttribute('aria-disabled');
  if (label instanceof HTMLElement && label.dataset.idleText !== undefined) {
    label.textContent = label.dataset.idleText;
    delete label.dataset.idleText;
  }
}

/** @param {string | undefined} message */
function announce(message) {
  const region = document.querySelector('[data-cs-cart-announcer]');
  if (!(region instanceof HTMLElement) || !message) return;

  region.textContent = '';
  requestAnimationFrame(() => {
    region.textContent = message;
  });
}

/**
 * Картка з натиснутою кнопкою зникає після додавання (товар тепер у кошику),
 * і фокус інакше падає на `<body>`. Переносимо його на заголовок блоку, звідки
 * Tab веде до стрілок каруселі й рекомендацій; якщо рекомендацій не лишилося — на
 * доданий рядок кошика. Після миші чи дотику — без прокрутки вмісту.
 * @param {string} variantId
 * @param {boolean} fromKeyboard
 */
function moveFocusAfterAdd(variantId, fromKeyboard) {
  const active = document.activeElement;
  const lost = !active || active === document.body || !active.isConnected;
  if (!fromKeyboard && !lost) return;

  const target =
    document.querySelector('#cart-drawer [data-cs-cart-recs] .cs-cart-recs__title') ??
    document.querySelector(`#cart-drawer [data-key^="${CSS.escape(variantId)}:"] .cart-items__title`) ??
    document.querySelector('#cart-drawer .theme-drawer__close-button');

  if (target instanceof HTMLElement) target.focus({ preventScroll: !fromKeyboard });
}

/** Повернути в картки стан, який морф скинув до серверного. */
function restoreCards() {
  for (const card of document.querySelectorAll('#cart-drawer [data-cs-rec]')) {
    if (!(card instanceof HTMLElement)) continue;
    const productId = card.dataset.productId ?? '';

    if (inFlight.has(productId)) setBusy(card, true);

    const select = selectOf(card);
    const stored = selections.get(productId);
    if (!select || !stored) continue;

    const stillOffered = Array.from(select.options).some((option) => option.value === stored && !option.disabled);
    if (!stillOffered) {
      // Варіант більше не доступний: не підставляємо замість нього інший.
      selections.delete(productId);
      continue;
    }

    if (select.value !== stored) select.value = stored;
    renderPrice(card);
  }
}

/**
 * Елемент, з якого слати події кошика. Зміна кошика під час запиту перебудовує
 * карусель новим екземпляром, і стара кнопка випадає з документа: подія з неї
 * не дійшла б до `document` і drawer не оновився б.
 * @param {string} productId
 * @param {HTMLElement} button
 * @returns {HTMLElement}
 */
function eventTarget(productId, button) {
  if (button.isConnected) return button;
  const current = cardOf(productId)?.querySelector('[data-cs-rec-add]');
  if (current instanceof HTMLElement) return current;
  return document.getElementById('cart-drawer') ?? document.body;
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * Кількість товарів із відрендереної секції drawer — запасний шлях, якщо
 * `/cart.js` не відповів (так само рахує `component-cart-items.js`).
 * @param {Record<string, string> | undefined} sections
 */
function itemCountFromSections(sections) {
  const html = sections?.[SECTION_ID];
  if (!html) return undefined;
  const count = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent;
  return count ? parseInt(count, 10) : 0;
}

/**
 * @param {HTMLElement} card
 * @param {HTMLButtonElement} button
 * @param {boolean} fromKeyboard
 */
async function addToCart(card, button, fromKeyboard) {
  const productId = card.dataset.productId ?? '';
  if (inFlight.has(productId)) return;

  const variantId = selectedVariantId(card);
  if (!variantId) {
    const select = selectOf(card);
    showError(card, card.dataset.chooseMessage ?? '');
    select?.setAttribute('aria-invalid', 'true');
    select?.focus();
    return;
  }

  const properties = selectedLineItemProperties(card);

  hideError(card);
  inFlight.add(productId);
  setBusy(card, true);

  const sectionIds = new Set();
  for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (component instanceof HTMLElement && component.dataset.sectionId) sectionIds.add(component.dataset.sectionId);
  }

  try {
    const response = await fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1, ...(properties ? { properties } : {}) }],
          sections: Array.from(sectionIds).join(','),
          sections_url: window.location.pathname,
        }),
      })
    );
    const data = await response.json();

    if (!response.ok || data.status) {
      // Shopify пише помилки кошика своїми системними мовами, української серед них
      // немає. Текст про залишок — лише коли його підтверджує текст Shopify, для решти
      // помилок — нейтральний текст картки. Сира відповідь Shopify — у консоль і подію.
      logCartError(SOURCE, response.status, data);
      showError(cardOf(productId) ?? card, cartErrorMessage(data, card.dataset.errorMessage));
      eventTarget(productId, button).dispatchEvent(
        new CartErrorEvent({
          error: shopifyCartErrorText(data) || 'Add to cart failed',
          code: 'INVALID',
          detail: { status: response.status, description: data.description, errors: data.errors },
        })
      );
      return;
    }

    // Товар уже в кошику. Навіть якщо `/cart.js` не відповість, drawer і
    // лічильник мають оновитися — інакше покупець натисне ще раз.
    let cart = null;
    try {
      cart = await fetchCart();
    } catch (error) {
      console.warn('[case-she-cart-drawer] Cart refresh failed after add:', error);
    }

    const promise = Promise.resolve({
      cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
      detail: {
        items: cart?.items ?? data.items,
        itemCount: cart?.item_count ?? itemCountFromSections(data.sections),
        sections: data.sections,
        source: SOURCE,
        didError: false,
      },
    });

    eventTarget(productId, button).dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise,
      })
    );

    await promise;
    await afterRender();

    announce(card.dataset.addedMessage);
    moveFocusAfterAdd(variantId, fromKeyboard);
  } catch (error) {
    console.error('[case-she-cart-drawer] Quick add failed:', error);
    showError(cardOf(productId) ?? card, card.dataset.errorMessage ?? '');
    eventTarget(productId, button).dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Network error during add to cart',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
  } finally {
    inFlight.delete(productId);
    // Після успіху картки вже немає; після помилки морф міг замінити її вміст.
    const current = cardOf(productId);
    if (current) setBusy(current, false);
  }
}

/** @param {Event} event */
function onChange(event) {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-cs-rec-select]')) return;

  const card = select.closest('[data-cs-rec]');
  if (!(card instanceof HTMLElement)) return;

  if (select.value) selections.set(card.dataset.productId ?? '', select.value);
  hideError(card);
  renderPrice(card);
}

/** @param {MouseEvent} event */
function onClick(event) {
  const button = event.target instanceof Element ? event.target.closest('[data-cs-rec-add]') : null;
  if (!(button instanceof HTMLButtonElement)) return;

  const card = button.closest('[data-cs-rec]');
  if (!(card instanceof HTMLElement)) return;

  event.preventDefault();
  // `detail === 0` — натискання Enter/Space або віртуальний клік скрінрідера.
  addToCart(card, button, event.detail === 0);
}

/**
 * `slideshow-component` Horizon (`#handleMouseDown` у `assets/slideshow.js`)
 * слухає `mousedown` на `slideshow-slides` і робить `preventDefault()`, щоб
 * тягнути слайди мишею. Для полів картки це ламає їх: нативний `<select>` не
 * відкривається, а після найменшого зсуву миші слайдшоу захоплює вказівник
 * (`setPointerCapture`) і `click` дістається каруселі замість кнопки «Додати».
 * Тож `mousedown` на полях і посиланнях карток каруселі drawer не пропускаємо
 * до слайдшоу. Решта картки (фото, порожнє місце) і всі інші каруселі Horizon
 * тягнуться як раніше; дотик гортає нативна прокрутка.
 * @param {MouseEvent} event
 */
function onMouseDownCapture(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const control = target.closest('select, button, a[href], input, label, textarea');
  if (!control || !control.closest('#cart-drawer [data-cs-cart-recs] [data-cs-rec]')) return;
  event.stopPropagation();
}

/** @param {Event & { promise?: Promise<unknown> }} event */
function onCartUpdate(event) {
  event.promise
    ?.then(afterRender)
    .then(restoreCards)
    .catch(() => {});
}

// Модуль виконується один раз на сторінку, а обробники делеговані на
// `document`, тож морф drawer їх не множить і не губить.
document.addEventListener('mousedown', onMouseDownCapture, { capture: true });
document.addEventListener('change', onChange);
document.addEventListener('click', onClick);
document.addEventListener(StandardEvents.cartLinesUpdate, onCartUpdate);
document.addEventListener(StandardEvents.cartDiscountUpdate, onCartUpdate);
